/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect } from "react";
import ThresholdCrossingSettings from "../../../components/Administrations/PerformanceSettings/ThresholdCrossingSettings/ThresholdCrossingSettings";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/tabs";
import { Badge } from "../../../components/ui/badge";
import { resetThresholdCrossingSettingProfile } from "../../../components/helpers";
import { useProfileList } from "./hooks";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { basicDialogSelector } from "../../../store/helpers/helperSelectors";
import AddEditProfile from "../../../components/Administrations/PerformanceSettings/ThresholdCrossingSettings/AddEditProfile";
import { thresholdCrossingSettingProfileModelOpenModeSelector } from "../../../store/api/admin/selectors";
import { setThresholdCrossingSettingProfileModelOpenMode } from "../../../store/api/admin/reducers";
import { useGetThresholdCrossingSettingProfileByIdQuery } from "../../../store/api/admin/adminApiSlice";
import ThresholdDevice from "../../../components/Administrations/PerformanceSettings/ThresholdDevice/ThresholdDevice";
import AssociationNavigation from "../../../components/Shared/Association/AssociationNavigation";
import { Ioptions } from "../../../components/Shared/Filter/types";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";

const AddProfileDialog: React.FC = () => {
  return null;
};

const PerformanceSettings: React.FC = () => {
  const dispatch = useStoreDispatch();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const canManageProfiles = !!permissions?.hasOpCodeGroup("performance_management");

  const { profileList, sortedProfileList, selectedProfile, setSelectedProfile, selectedId, setSelectedId, deleteThresholdCrossingSettingProfileClickAction, deleteProfile, setNewlyCreatedProfileId } = useProfileList();

  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const thresholdCrossingSettingProfileModelOpenMode = useSelector(thresholdCrossingSettingProfileModelOpenModeSelector);
  const { data, refetch: profileRefetch } =
    useGetThresholdCrossingSettingProfileByIdQuery({ id: selectedId }, { skip: selectedId === 0 });

  useEffect(() => {
    if (selectedId !== 0) LoadProfileData();
  }, [selectedId, profileList]);

  useEffect(() => {
    if (selectedId === 0) setSelectedId(selectedProfile.id);
  }, [selectedProfile]);

  const LoadProfileData = async () => {
    if (data) {
      const { data: newData } = await profileRefetch();
      if (newData) {
        setSelectedProfile(newData);
      }
    }
  };

  // Map profiles to Ioptions for AssociationNavigation
  const navItems: Ioptions[] = sortedProfileList(profileList).map((p) => ({
    text: p.name,
    value: p.id,
  }));
  const selectedItem: Ioptions | undefined = selectedId
    ? { text: selectedProfile.name, value: selectedId }
    : undefined;

  const handleNavItemClick = (item: Ioptions) => {
    setSelectedId(item.value);
  };

  const handleDeleteProfile = (item: Ioptions) => {
    const profile = profileList.find((p) => p.id === item.value);
    if (profile) {
      deleteThresholdCrossingSettingProfileClickAction("yes, delete", profile);
    }
  };

  const handleDeleteProfileDirect = () => {
    deleteProfile(selectedProfile);
    setSelectedId(0);
  };

  const tabItems = [
    {
      value: "threshold-crossing-settings",
      label: "Threshold Crossing Settings",
      component: (
        <ThresholdCrossingSettings
          selectedProfileRow={selectedProfile}
          onDeleteProfile={handleDeleteProfileDirect}
          canManage={canManageProfiles}
        />
      ),
    },
    {
      value: "devices",
      label: "Devices",
      component: <ThresholdDevice selectedProfile={selectedProfile} canManage={canManageProfiles} onAssociationChanged={LoadProfileData} />,
    },
  ];

  const handleTabChange = (value: string) => {
    if (value === "threshold-crossing-settings") {
      LoadProfileData();
    }
  };

  const MAX_PROFILES_SEARCH = 1;
  return (
    <>
      {basicDialogOpenFor === DIALOGOPENFOR.THRESHOLDCROSSINGSETTINGPROFILE && (
        <AddEditProfile
          modelOpenMode={thresholdCrossingSettingProfileModelOpenMode}
          selectedRow={thresholdCrossingSettingProfileModelOpenMode === "Add" ? resetThresholdCrossingSettingProfile : selectedProfile}
          onProfileCreated={setNewlyCreatedProfileId}
        />
      )}
      <div className="flex flex-row flex-nowrap">
        <AssociationNavigation
          searchPlaceholder={"Search profiles..."}
          navHeaderName={"TCA profiles"}
          selectedItemIndex={selectedItem}
          isManagePermission={canManageProfiles}
          navItems={navItems}
          ShowSearchFilterOnRecordsCount={MAX_PROFILES_SEARCH}
          addNavItem={AddProfileDialog}
          confirmDeleteDialogOpener={DIALOGOPENFOR.DELETETHRESHOLDCROSSINGSETTINGPROFILE}
          deleteConfirmationMessage={"Are you sure you want to delete profile"}
          addDialogOpener={DIALOGOPENFOR.THRESHOLDCROSSINGSETTINGPROFILE}
          handleNavItemClick={handleNavItemClick}
          deleteNavItemHandler={handleDeleteProfile}
          isCountBadgeVisible={false}
          isDeleteIconVisible={false}
        />
        <div className="flex-1 pt-2 ml-5">
          <Tabs key={selectedId} defaultValue={tabItems[0].value} onValueChange={handleTabChange} className="w-full flex object-cover flex-col items-start gap-0.5">
            <div className="flex items-center justify-between w-full">
              <TabsList className="inline-flex h-9 w-fit shrink-0 self-start items-stretch gap-0.5 rounded-full border border-border bg-muted p-0.5 text-muted-foreground">
                {tabItems.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="rounded-full !bg-transparent px-4 h-full inline-flex items-center justify-center text-sm font-medium text-muted-foreground !shadow-none transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:!bg-card data-[state=active]:text-foreground data-[state=active]:!shadow-sm"
                  >
                    <span>{tab.label}</span>
                    {tab.value === "devices" && selectedProfile && (
                      <Badge variant="secondary" className="ml-2 font-semibold transition-colors text-xs">
                        {selectedProfile.devices?.length || 0}
                      </Badge>
                    )}

                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            
            <div className="w-full">
              {tabItems.map((tab) => (
                <TabsContent key={tab.value} value={tab.value} className="m-0" style={{ width: "99%" }}>
                  {tab.component}
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default PerformanceSettings;